import { Capacitor } from '@capacitor/core';
import { CordovaPlugins } from '../../../../shared/CordovaPlugins';
import { logMobileUsage } from '../../../../shared/utils/mobileUsageLogging';

interface ImageQualityConfig {
    maxSize: number;
    centerCropFraction: number;
    blockVarianceBlockSize: number;
    gradientThreshold: number;
    BlockVarianceMinThreshold: number;
    laplacianVarianceMinThreshold: number;
}

interface GrayscaleValues {
    grayPixels: Float32Array;
    width: number;
    height: number;
}

interface ImageQualityAnalysisResult {
    isBad: boolean;
    laplacianVariance: number;
    gradientNinetyFifthPercentile: number;
    meanBlockVariance: number;
}

const shouldConsoleLog = !Capacitor.isNativePlatform()
    ? process.env.DEPLOY_ENV !== 'production'
    : CordovaPlugins().BuildInfo.debug;

// Load File/Blob into a resized canvas
const loadImageToCanvas = async (filePath: string, config: ImageQualityConfig): Promise<HTMLCanvasElement> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const fullW = img.width;
            const fullH = img.height;

            const f = Math.min(1, Math.max(0.1, config.centerCropFraction));

            const cropW = fullW * f;
            const cropH = fullH * f;
            const sx = (fullW - cropW) / 2;
            const sy = (fullH - cropH) / 2;

            const canvas = document.createElement('canvas');
            const scale = Math.min(1, config.maxSize / Math.max(cropW, cropH));
            const targetW = Math.round(cropW * scale);
            const targetH = Math.round(cropH * scale);

            canvas.width = targetW;
            canvas.height = targetH;

            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            ctx.drawImage(img, sx, sy, cropW, cropH, 0, 0, targetW, targetH);

            resolve(canvas);
        };
        img.onerror = reject;
        img.src = filePath;
    });
};

// Convert canvas to grayscale array
const getGrayscalePixels = (canvas: HTMLCanvasElement): GrayscaleValues => {
    const context = canvas.getContext('2d');
    const { width, height } = canvas;
    const { data: rgbaPixels } = context.getImageData(0, 0, width, height);

    const grayPixels = new Float32Array(width * height);

    for (let pixelIndex = 0, grayIndex = 0; pixelIndex < rgbaPixels.length; pixelIndex += 4, grayIndex++) {
        const red = rgbaPixels[pixelIndex];
        const green = rgbaPixels[pixelIndex + 1];
        const blue = rgbaPixels[pixelIndex + 2];

        // Perceived luminance formula: output is 0–255
        grayPixels[grayIndex] = 0.299 * red + 0.587 * green + 0.114 * blue;
    }

    return { grayPixels, width, height };
};

/**
 * Applies a small Gaussian blur to a grayscale image.
 *
 * Kernel:
 *   1  2  1
 *   2  4  2   (normalized by 1/16)
 *   1  2  1
 *
 * This approximates a Gaussian with σ ≈ 1 and is enough to
 * suppress high-frequency sensor noise without destroying edges.
 */
const applyGaussianBlur = (grayscale: GrayscaleValues): GrayscaleValues => {
    const { grayPixels, width, height } = grayscale;
    const blurred = new Float32Array(width * height);

    // Copy borders as-is to avoid extra bounds checks / padding logic
    for (let x = 0; x < width; x++) {
        blurred[x] = grayPixels[x]; // top row
        blurred[(height - 1) * width + x] = grayPixels[(height - 1) * width + x]; // bottom row
    }
    for (let y = 0; y < height; y++) {
        blurred[y * width] = grayPixels[y * width]; // left column
        blurred[y * width + (width - 1)] = grayPixels[y * width + (width - 1)]; // right column
    }

    // Convolve interior pixels with the 3x3 Gaussian kernel
    for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
            const idx = y * width + x;

            const upIdx = (y - 1) * width + x;
            const downIdx = (y + 1) * width + x;

            const upLeft = grayPixels[upIdx - 1];
            const upCenter = grayPixels[upIdx];
            const upRight = grayPixels[upIdx + 1];

            const midLeft = grayPixels[idx - 1];
            const midCenter = grayPixels[idx];
            const midRight = grayPixels[idx + 1];

            const downLeft = grayPixels[downIdx - 1];
            const downCenter = grayPixels[downIdx];
            const downRight = grayPixels[downIdx + 1];

            const weightedSum =
                1 * upLeft +
                2 * upCenter +
                1 * upRight +
                2 * midLeft +
                4 * midCenter +
                2 * midRight +
                1 * downLeft +
                2 * downCenter +
                1 * downRight;

            blurred[idx] = weightedSum / 16;
        }
    }

    return {
        grayPixels: blurred,
        width,
        height
    };
};

/**
 * Computes the variance of the Laplacian over a grayscale image.
 *
 * This metric is commonly used for **sharpness / blur detection**:
 * - High variance   → many strong edges → image is sharp
 * - Low variance    → few weak edges    → image is blurry
 *
 * Input image must already be converted to grayscale since the Laplacian
 * operates on intensity, not color.
 */
const getLaplacianVariance = (grayscaleValues: GrayscaleValues): number => {
    const { grayPixels, width, height } = grayscaleValues;

    // Sum of all Laplacian responses
    let laplacianSum = 0;

    // Sum of squares of Laplacian responses (used later for variance calculation)
    let laplacianSumSquared = 0;

    // Counter for valid processed pixels
    let laplacianCount = 0;

    /**
     * We skip the outer 1-pixel border to avoid:
     * - Out-of-bounds memory access
     * - Needing special edge-handling logic (padding, mirroring, etc.)
     */
    for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
            const index = y * width + x;

            /**
             * Discrete 4-neighbor Laplacian operator:
             *
             *      0  1  0
             *      1 -4  1   → highlights areas of rapid intensity change (edges)
             *      0  1  0
             *
             * This measures the second spatial derivative of image intensity.
             */
            const laplacian =
                grayPixels[index - width] + // top
                grayPixels[index + width] + // bottom
                grayPixels[index - 1] + // left
                grayPixels[index + 1] - // right
                4 * grayPixels[index]; // center

            laplacianSum += laplacian;
            laplacianSumSquared += laplacian * laplacian;
            laplacianCount++;
        }
    }

    // Safety check — should only trigger for extremely small images
    if (laplacianCount === 0) return 0;

    /**
     * Variance formula:
     *
     * Var(X) = E[X²] − (E[X])²
     *
     * This avoids storing every Laplacian value in memory.
     */
    const meanLaplacian = laplacianSum / laplacianCount;
    const variance = laplacianSumSquared / laplacianCount - meanLaplacian * meanLaplacian;

    // Final sharpness score — thresholdable for blur detection
    return variance;
};

/**
 * Computes a robust gradient-based sharpness measure using a percentile of gradient magnitudes.
 * Interpretation:
 * - Higher percentile value → stronger edges present → sharper subject / image.
 * - Lower percentile value  → weaker edges overall  → blurrier image.
 */
const getGradientPercentile = (grayscaleValues: GrayscaleValues, percentile: number = 0.95): number => {
    const { grayPixels, width, height } = grayscaleValues;

    // Collect gradient magnitudes for all interior pixels.
    // This enables us to compute arbitrary percentiles.
    const magnitudes: number[] = [];

    // Skip the 1-pixel border for the same reason as before:
    // avoid bounds checks and special casing.
    for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
            const index = y * width + x;

            // Central differences:
            // gx: left-right intensity change
            // gy: top-bottom intensity change
            const gx = grayPixels[index + 1] - grayPixels[index - 1];
            const gy = grayPixels[index + width] - grayPixels[index - width];

            // Gradient magnitude: √(gx² + gy²)
            const magnitude = Math.sqrt(gx * gx + gy * gy);

            magnitudes.push(magnitude);
        }
    }

    if (magnitudes.length === 0) return 0;

    // Sort ascending so we can index into the CDF and pick a percentile.
    magnitudes.sort((a, b) => a - b);

    // Clamp percentile to [0, 1] to avoid NaNs / out-of-range indexing.
    const p = Math.min(1, Math.max(0, percentile));

    /**
     * Convert percentile to rank index.
     *
     * For N values sorted ascending:
     *  - 0.0 → index 0        (minimum)
     *  - 1.0 → index N - 1    (maximum)
     *  - 0.95 → index ≈ 0.95 * (N - 1)
     */
    const rank = Math.floor(p * (magnitudes.length - 1));

    return magnitudes[rank];
};

/**
 * Computes the mean local intensity variance over fixed-size blocks.
 *
 * Why this exists (high-level purpose):
 *
 * Edge-based metrics (Laplacian, gradients) answer:
 *   → "How strong and how frequent are edges?"
 *
 * This block-variance metric answers a different question:
 *   → "How much real tonal structure and texture exists inside image regions?"
 *
 * In practice this helps detect:
 * - Over-smoothed images that still contain edges
 * - Flat, dead images with no local contrast
 * - Lighting-collapsed frames (fog, overexposure, heavy blur, downscaling)
 * - Texture loss that Laplacian and gradients can miss
 *
 * Interpretation:
 *
 * - High mean block variance  → many blocks contain real tonal variation
 *                             → healthy texture & local contrast
 * - Low mean block variance   → many flat blocks
 *                             → blur, aggressive denoising, or low-detail scene
 */
const getBlockVariance = (grayscaleValues: GrayscaleValues, config: ImageQualityConfig): number => {
    const { width, height, grayPixels } = grayscaleValues;
    const blockSize = config.blockVarianceBlockSize;

    /**
     * Number of full, non-overlapping blocks that fit in the image.
     * Any remainder pixels on the right/bottom edges are ignored to:
     * - keep indexing simple
     * - avoid padding or partial-block bias
     */
    const blocksHoriz = Math.floor(width / blockSize);
    const blocksVert = Math.floor(height / blockSize);

    if (blocksHoriz < 1 || blocksVert < 1) {
        // Image is too small to form even a single statistical block
        return 0;
    }

    const blockVariances: number[] = [];

    // Iterate over the block grid
    for (let by = 0; by < blocksVert; by++) {
        for (let bx = 0; bx < blocksHoriz; bx++) {
            let sum = 0;
            let sumSquared = 0;
            let pixelCount = 0;

            const startX = bx * blockSize;
            const startY = by * blockSize;

            /**
             * Accumulate pixel statistics inside the current block.
             * We compute:
             *   - Σx     → mean
             *   - Σx²    → variance
             */
            for (let y = 0; y < blockSize; y++) {
                const row = startY + y;
                const rowOffset = row * width;

                for (let x = 0; x < blockSize; x++) {
                    const col = startX + x;
                    const index = rowOffset + col;
                    const value = grayPixels[index];

                    sum += value;
                    sumSquared += value * value;
                    pixelCount++;
                }
            }

            /**
             * Local block variance:
             * Var(X) = E[X²] − (E[X])²
             *
             * This captures how much tonal variation exists inside this block,
             * regardless of whether that variation forms a sharp edge.
             */
            const mean = sum / pixelCount;
            const variance = sumSquared / pixelCount - mean * mean;

            // Clamp to avoid tiny negative values from floating-point drift
            blockVariances.push(Math.max(variance, 0));
        }
    }

    /**
     * Final score = mean of all block variances.
     *
     * This represents average local texture / contrast health
     * across the entire image, independent of edge count.
     */
    let sumVar = 0;
    for (const v of blockVariances) {
        sumVar += v;
    }

    const blockCount = blockVariances.length;
    const meanVariance = sumVar / blockCount;

    return meanVariance;
};

const analyzeCanvas = (canvas: HTMLCanvasElement, config: ImageQualityConfig): ImageQualityAnalysisResult => {
    const grayscaleValues = getGrayscalePixels(canvas);

    // Pre-smooth to reduce high-frequency noise
    const grayscaleValuesBlurred = applyGaussianBlur(grayscaleValues);

    const laplacianVariance = getLaplacianVariance(grayscaleValuesBlurred);
    const gradientNinetyFifthPercentile = getGradientPercentile(grayscaleValuesBlurred, 0.95);
    const meanBlockVariance = getBlockVariance(grayscaleValuesBlurred, config);

    const isBad =
        gradientNinetyFifthPercentile <= config.gradientThreshold &&
        (meanBlockVariance <= config.BlockVarianceMinThreshold ||
            laplacianVariance <= config.laplacianVarianceMinThreshold);

    const result = {
        isBad,
        gradientNinetyFifthPercentile,
        laplacianVariance,
        meanBlockVariance
    };

    /* eslint-disable no-console */
    if (shouldConsoleLog) {
        console.log(result);
    }

    return result;
};

const analyzeImageQuality = async (
    filePath: string,
    config: ImageQualityConfig | undefined = undefined
): Promise<ImageQualityAnalysisResult> => {
    const ImageQualityConfig = config ?? {
        maxSize: 800,
        centerCropFraction: 0.6,
        blockVarianceBlockSize: 8,
        gradientThreshold: 9,
        BlockVarianceMinThreshold: 29,
        laplacianVarianceMinThreshold: 10
    };

    const canvasStandard = await loadImageToCanvas(filePath, ImageQualityConfig);

    const standardResults = analyzeCanvas(canvasStandard, ImageQualityConfig);

    const goodPhotoCustomEventName = 'goodPhotoDetected';
    const badPhotoCustomEventName = 'badPhotoDetected';

    // double check the image by cropping into the center
    // as there might be bokeh effect causing the image to be recognized as blurry
    if (standardResults.isBad) {
        ImageQualityConfig.centerCropFraction = 0.3;
        const canvasCenterCropped = await loadImageToCanvas(filePath, ImageQualityConfig);

        const croppedResults = analyzeCanvas(canvasCenterCropped, ImageQualityConfig);

        if (!croppedResults.isBad) {
            logMobileUsage(goodPhotoCustomEventName, croppedResults);
            // the object in the center is in focus
            return croppedResults;
        }
    }

    logMobileUsage(standardResults.isBad ? badPhotoCustomEventName : goodPhotoCustomEventName, standardResults);
    return standardResults;
};

export { analyzeImageQuality };
